/**
 * Bob Laadt Op — bob-steps (stappen-tijdlijn, installatie).
 * Bron: _design/Bob-laadt-op-thomas/werkbestanden/paginas/installatie-stappenplan.html r. 1308–1324.
 *
 * De voortgangslijn vult op scroll tot 60% van de viewport, en elke stap waarvan het midden
 * daarboven ligt krijgt .is-active (oranje cirkel).
 * Mobiel (≤700px, foto en tekst onder elkaar): daar telt het midden van de cirkel zelf. Een stap
 * met beslisvisual is daar 1000px+ hoog; met het stapmidden was de cirkel (bovenaan) al uit beeld
 * voordat hij oranje werd.
 * Scrollpositie via window.bobScroll (Horizon scrollt vanaf 990px .page-wrapper, niet het venster).
 * In de editor: shopify:block:select zet de gekozen stap vast tot hij gedeselecteerd wordt.
 * Custom element, zodat het na een re-render in de theme editor opnieuw start.
 */
class BobSteps extends HTMLElement {
  constructor() {
    super();
    this.update = this.update.bind(this);
    this.onBlockSelect = this.onBlockSelect.bind(this);
    this.onBlockDeselect = this.onBlockDeselect.bind(this);
    /** @type {number | null} stap die in de editor geselecteerd is */
    this.selected = null;
    this.mobile = window.matchMedia('(max-width: 700px)');
  }

  connectedCallback() {
    this.list = /** @type {HTMLElement | null} */ (this.querySelector('.steps-list'));
    this.fill = /** @type {HTMLElement | null} */ (this.querySelector('.steps-line-fill'));
    if (!this.list || !this.fill) return;

    this.addEventListener('shopify:block:select', this.onBlockSelect);
    this.addEventListener('shopify:block:deselect', this.onBlockDeselect);

    if (window.bobScroll) {
      window.bobScroll.on(this.update);
    } else {
      document.addEventListener('scroll', this.update, { passive: true, capture: true });
    }
    window.addEventListener('resize', this.update);
    this.update();
  }

  disconnectedCallback() {
    if (window.bobScroll) {
      window.bobScroll.off(this.update);
    } else {
      document.removeEventListener('scroll', this.update, { capture: true });
    }
    window.removeEventListener('resize', this.update);
    this.removeEventListener('shopify:block:select', this.onBlockSelect);
    this.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
  }

  get steps() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('.step-item')]);
  }

  update() {
    if (!this.list || !this.fill) return;
    const steps = this.steps;
    const rect = this.list.getBoundingClientRect();
    if (!rect.height) return;

    // Editor: de gekozen stap wordt het "midden" — lijn vult tot die stap, hij en alles erboven licht op.
    if (this.selected !== null && steps[this.selected]) {
      const step = steps[this.selected];
      const stepMid = step.getBoundingClientRect().top + step.offsetHeight / 2;
      const progress = Math.min(1, Math.max(0, (stepMid - rect.top) / rect.height));
      this.fill.style.height = progress * 100 + '%';
      steps.forEach((el, i) => el.classList.toggle('is-active', i <= /** @type {number} */ (this.selected)));
      return;
    }

    const viewportMid = innerHeight * 0.6;
    const progress = Math.min(1, Math.max(0, (viewportMid - rect.top) / rect.height));
    this.fill.style.height = progress * 100 + '%';
    steps.forEach((step) => {
      const num = this.mobile.matches ? step.querySelector('.step-num') : null;
      const r = (num || step).getBoundingClientRect();
      step.classList.toggle('is-active', r.top + r.height / 2 < viewportMid);
    });
  }

  /** @param {Event} event */
  onBlockSelect(event) {
    const step = /** @type {Element} */ (event.target).closest?.('.step-item');
    if (!step) return;
    const index = this.steps.indexOf(/** @type {HTMLElement} */ (step));
    this.selected = index < 0 ? null : index;
    this.update();
  }

  onBlockDeselect() {
    this.selected = null;
    this.update();
  }
}

if (!customElements.get('bob-steps')) {
  customElements.define('bob-steps', BobSteps);
}
